/* SIZESOFTX
=========================================================================================
* Descripción: Servicios API del panel de soporte SizeSoftX (master)
* Revisión 18/09/2026 | José Luis Aragón Cervantes | Flujo Kanban, seguimiento y privacidad.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 11/09/2026 | Lic. José Luis Aragón Cervantes  | Creación
* ---------------------------------------------------------------------------------------
*/

import supportApi, { supportPublicApi, unwrap } from "./ApiConfig";
import {
  apiManualesSupportList,
  apiManualSupportDetalle,
  apiManualSupportGuardar,
  apiManualSupportActualizar,
  apiManualSupportEliminar,
  apiManualSupportImagen,
  apiManualSupportPublicar,
  apiManualSupportConocimientoIniciar,
  apiManualSupportConocimientoEstado,
  apiManualSupportCerrarConversacion,
  apiSupportAgentConfig,
  apiSupportAgentConfigGuardar,
} from "./n8nWebhookApi";

// Manuales y configuración del agente van al backend (SupportManualesController,
// rol SA), que reenvía a n8n VPS2 con el secreto del servidor. El navegador
// nunca llama a n8n: sin CORS ni tokens en el bundle.
// Accesos (login master contra la tabla Accesos de TenantSizeSoftX)
export const apiLogin = (usuario, password) =>
  supportApi.post("/SupportAccess/login", { usuario, password }).then(unwrap);

export const apiLogout = () => supportApi.post("/SupportAccess/logout").then(unwrap);
export {
  apiManualesSupportList,
  apiManualSupportDetalle,
  apiManualSupportGuardar,
  apiManualSupportActualizar,
  apiManualSupportEliminar,
  apiManualSupportImagen,
  apiManualSupportPublicar,
  apiManualSupportConocimientoIniciar,
  apiManualSupportConocimientoEstado,
  apiManualSupportCerrarConversacion,
  apiSupportAgentConfig,
  apiSupportAgentConfigGuardar,
};
export const apiPerfil = () => supportApi.get("/SupportAccess/perfil").then(unwrap);

export const apiUsuariosList = () => supportApi.get("/SupportAccess/usuarios").then(unwrap);

export const apiRolesList = () => supportApi.get("/SupportAccess/roles").then(unwrap);

export const apiUsuarioCrear = (payload) =>
  supportApi.post("/SupportAccess/usuarios", payload).then(unwrap);

export const apiUsuarioActualizar = (payload) =>
  supportApi.put("/SupportAccess/usuarios", payload).then(unwrap);

export const apiUsuarioRestablecerPassword = (id, password) =>
  supportApi
    .post(`/SupportAccess/usuarios/${id}/restablecer-password`, { password })
    .then(unwrap);

// Panel maestro
export const apiResumen = () => supportApi.get("/SupportAdmin/resumen").then(unwrap);

export const apiTenantsList = (params) =>
  supportApi.get("/SupportAdmin/tenants", { params }).then(unwrap);

export const apiTenantDetalle = (id) =>
  supportApi.get(`/SupportAdmin/tenants/${id}`).then(unwrap);

export const apiTenantMaxDevices = (id, payload) =>
  supportApi.put(`/SupportAdmin/tenants/${id}/maxdevices`, payload).then(unwrap);

export const apiEquipoBloquear = (tenantId, devicePublicId, payload) =>
  supportApi
    .post(`/SupportAdmin/tenants/${tenantId}/equipos/${devicePublicId}/bloquear`, payload)
    .then(unwrap);

export const apiEquipoDesbloquear = (tenantId, devicePublicId) =>
  supportApi
    .post(`/SupportAdmin/tenants/${tenantId}/equipos/${devicePublicId}/desbloquear`)
    .then(unwrap);

export const apiSolicitudAutorizar = (tenantId, solicitudId) =>
  supportApi.post(`/SupportAdmin/tenants/${tenantId}/solicitudes/${solicitudId}/autorizar`).then(unwrap);

export const apiSolicitudRevocar = (tenantId, solicitudId) =>
  supportApi.post(`/SupportAdmin/tenants/${tenantId}/solicitudes/${solicitudId}/revocar`).then(unwrap);

export const apiEstadoCuenta = (tenantId) =>
  supportApi.get(`/SupportAdmin/tenants/${tenantId}/estadocuenta`).then(unwrap);

export const apiPagoRegistrar = (tenantId, payload) =>
  supportApi.post(`/SupportAdmin/tenants/${tenantId}/pagos`, payload).then(unwrap);

export const apiComunicaciones = () => supportApi.get("/SupportAdmin/comunicaciones").then(unwrap);

export const apiBitacora = () => supportApi.get("/SupportAdmin/bitacora").then(unwrap);

export const apiLicenciaReactivar = (tenantId, payload) =>
  supportApi.post(`/SupportAdmin/tenants/${tenantId}/licencias/reactivar`, payload).then(unwrap);

export const apiLicenciaRestarDias = (tenantId, payload) =>
  supportApi.post(`/SupportAdmin/tenants/${tenantId}/licencias/restar-dias`, payload).then(unwrap);

export const apiLicenciaExtenderVigencia = (tenantId, payload) =>
  supportApi.post(`/SupportAdmin/tenants/${tenantId}/licencias/extender-vigencia`, payload).then(unwrap);

export const apiLicenciaSolicitarTitularidad = (tenantId, payload) =>
  supportApi.post(`/SupportAdmin/tenants/${tenantId}/licencias/validar-titularidad/enviar`, payload).then(unwrap);

export const apiLicenciaConfirmarTitularidad = (tenantId, payload) =>
  supportApi.post(`/SupportAdmin/tenants/${tenantId}/licencias/validar-titularidad/confirmar`, payload).then(unwrap);

export const apiLicenciasSincronizar = (tenantId) =>
  supportApi.post(`/SupportAdmin/tenants/${tenantId}/licencias/sincronizar`).then(unwrap);

export const apiEquipoEliminar = (tenantId, devicePublicId) =>
  supportApi.delete(`/SupportAdmin/tenants/${tenantId}/equipos/${devicePublicId}`).then(unwrap);

export const apiCorreoContrato = (tenantId, payload) =>
  supportApi.post(`/SupportAdmin/tenants/${tenantId}/correos/contrato`, payload).then(unwrap);

export const apiCorreoEstadoCuenta = (tenantId, payload) =>
  supportApi.post(`/SupportAdmin/tenants/${tenantId}/correos/estadocuenta`, payload).then(unwrap);

export const apiValidacionLicencias = (dias = 15) =>
  supportApi.get("/SupportAdmin/licencias/validacion", { params: { dias } }).then(unwrap);

export const apiNotificarVencimiento = (payload) =>
  supportApi.post("/SupportAdmin/licencias/notificacion-vencimiento", payload).then(unwrap);
export const apiLicenciasLiberacionBuscar = (params) =>
  supportApi.get("/SupportAdmin/licencias/liberacion/buscar", { params }).then(unwrap);

export const apiLicenciaLiberacionDetalle = (id) =>
  supportApi.get(`/SupportAdmin/licencias/liberacion/${id}`).then(unwrap);

export const apiValidacionTitularidadEnviar = (payload) =>
  supportApi.post("/SupportAdmin/licencias/liberacion/correo-validacion", payload).then(unwrap);

export const apiValidacionTitularidadConfirmarLiberacion = (payload) =>
  supportApi.post("/SupportAdmin/licencias/liberacion/confirmar-validacion", payload).then(unwrap);

export const apiLicenciaLiberar = (payload) =>
  supportApi.post("/SupportAdmin/licencias/liberacion/liberar", payload).then(unwrap);

// Invitaciones de licencia con contrato de uso
export const apiInvitacionesList = (params) =>
  supportApi.get("/SupportAdmin/invitaciones", { params }).then(unwrap);

export const apiInvitacionesDisponibles = (params) =>
  supportApi.get("/SupportAdmin/invitaciones/licencias-disponibles", { params }).then(unwrap);

export const apiInvitacionValidarLicencia = (licenseCode) =>
  supportApi.get("/SupportAdmin/invitaciones/licencia-validar", { params: { licenseCode } }).then(unwrap);

export const apiInvitacionDetalle = (id) =>
  supportApi.get(`/SupportAdmin/invitaciones/${id}`).then(unwrap);

export const apiInvitacionCrear = (payload) =>
  supportApi.post("/SupportAdmin/invitaciones", payload).then(unwrap);

export const apiInvitacionEnviarContrato = (id) =>
  supportApi.post(`/SupportAdmin/invitaciones/${id}/enviar-contrato`).then(unwrap);

export const apiInvitacionReenviar = (id) =>
  supportApi.post(`/SupportAdmin/invitaciones/${id}/reenviar`).then(unwrap);

export const apiInvitacionCancelar = (id, payload) =>
  supportApi.post(`/SupportAdmin/invitaciones/${id}/cancelar`, payload).then(unwrap);

export const apiContratoPublico = (token) =>
  supportPublicApi.get(`/SupportInvitacionesPublicas/contrato/${encodeURIComponent(token)}`).then(unwrap);

export const apiContratoAceptar = (token, payload) =>
  supportPublicApi.post(`/SupportInvitacionesPublicas/contrato/${encodeURIComponent(token)}/aceptar`, payload).then(unwrap);

// Campañas públicas ARSync / CRM
export const apiCampaniaPublica = (codigo) =>
  supportPublicApi.get(`/SupportCampaniasPublicas/${encodeURIComponent(codigo)}`).then(unwrap);

export const apiCampaniaVisita = (codigo, payload) =>
  supportPublicApi
    .post(`/SupportCampaniasPublicas/${encodeURIComponent(codigo)}/visita`, payload)
    .then(unwrap);

export const apiCampaniaLead = (codigo, payload) =>
  supportPublicApi
    .post(`/SupportCampaniasPublicas/${encodeURIComponent(codigo)}/lead`, payload)
    .then(unwrap);

export const apiCampaniasAdmin = (params) =>
  supportApi.get("/SupportCampaniasAdmin/campanias", { params }).then(unwrap);

export const apiCampaniaAdminDetalle = (id) =>
  supportApi.get(`/SupportCampaniasAdmin/campanias/${id}`).then(unwrap);

export const apiCampaniaCrear = (payload) =>
  supportApi.post("/SupportCampaniasAdmin/campanias", payload).then(unwrap);

export const apiCampaniaActualizar = (id, payload) =>
  supportApi.put(`/SupportCampaniasAdmin/campanias/${id}`, payload).then(unwrap);

export const apiCampaniaPublicar = (id, payload) =>
  supportApi.post(`/SupportCampaniasAdmin/campanias/${id}/publicar`, payload).then(unwrap);

export const apiCampaniaSubirImagen = (id, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return supportApi.post(`/SupportCampaniasAdmin/campanias/${id}/imagen`, formData).then(unwrap);
};

export const apiCampaniasProspectos = (params) =>
  supportApi.get("/SupportCampaniasAdmin/prospectos", { params }).then(unwrap);

// Tickets de soporte Tenant <-> Support
export const apiTicketsList = (params) =>
  supportApi.get("/SupportTickets", { params }).then(unwrap);

export const apiTicketDetalle = (id) =>
  supportApi.get(`/SupportTickets/${id}`).then(unwrap);

export const apiTicketResponder = (id, { mensaje, enlaces, esInterno, archivos }) => {
  const formData = new FormData();
  formData.append("mensaje", mensaje ?? "");
  formData.append("enlaces", enlaces ?? "");
  formData.append("esInterno", esInterno ? "true" : "false");
  (archivos || []).slice(0, 5).forEach((f) => formData.append("archivos", f));
  return supportApi.post(`/SupportTickets/${id}/responder`, formData).then(unwrap);
};

export const apiTicketEstatus = (id, status) =>
  supportApi.put(`/SupportTickets/${id}/estatus`, { sStatus: status }).then(unwrap);

export const apiTicketPrioridad = (id, prioridad) =>
  supportApi.put(`/SupportTickets/${id}/prioridad`, { sPrioridad: prioridad }).then(unwrap);

export const apiTicketAdjuntoDescargar = async (adjuntoId, nombre) => {
  const r = await supportApi.get(`/SupportTickets/adjuntos/${adjuntoId}`, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre || "adjunto";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

// 18/09/2026 | José Luis Aragón Cervantes | Gestión y responsables del tablero.
export const apiTicketGestion = (id, payload) => supportApi.put(`/SupportTickets/${id}/gestion`, payload).then(unwrap);
export const apiTicketResponsables = () => supportApi.get("/SupportTickets/responsables").then(unwrap);
